import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { ProfileContext } from "../../../context/ProfileProvider";
import "./passkey.css";

/**
 * Passkey page — static markup + fully scoped CSS.
 * Route: /user_profile/passkey
 */
const PasskeyPage = () => {
  const navigate = useNavigate();
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = useMemo(() => profileCtx.userDetails || {}, [profileCtx.userDetails]);
  const handleUserDetails = profileCtx.handleUserDetails || (() => { });

  const emailId = userDetails?.emailId || "";
  const mobileNumber = userDetails?.mobileNumber || "";
  const hasGoogleAuth = (userDetails?.["2fa"] || 0) === 2;
  // “Email” factor: if API tracks emailVerifiedAt, need verified; otherwise having email is enough (match backend).
  const isEmailSatisfied = useMemo(() => {
    if (!emailId) return false;
    if (!Object.prototype.hasOwnProperty.call(userDetails, "emailVerifiedAt")) {
      return true;
    }
    return !!userDetails?.emailVerifiedAt;
  }, [userDetails, emailId]);
  const hasMobile = !!userDetails?.mobileNumber;

  // Passkey management state
  const [passkeys, setPasskeys] = useState([]);
  const [isLoadingPasskeys, setIsLoadingPasskeys] = useState(false);
  const [selectedPasskey, setSelectedPasskey] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchPasskeys = useCallback(async () => {
    try {
      setIsLoadingPasskeys(true);
      const result = await AuthService.passkeyGetList();
      if (result?.success && result?.data != null) {
        const d = result.data;
        const list = Array.isArray(d) ? d : d.passkeys;
        setPasskeys(Array.isArray(list) ? list : []);
      } else {
        setPasskeys([]);
      }
    } catch {
      setPasskeys([]);
    } finally {
      setIsLoadingPasskeys(false);
    }
  }, []);

  useEffect(() => {
    fetchPasskeys();
  }, [fetchPasskeys]);

  // Show manage list only when the server has returned at least one passkey. Empty list = original
  // "Add a Passkey" benefits section (same layout as the first static design).
  const hasPasskeys = passkeys.length > 0;

  // Same 4 methods as security settings: email, mobile, Google Authenticator, passkey. Any 2 = enough to add a passkey.
  const activeSecurityCount = useMemo(
    () => [isEmailSatisfied, hasMobile, hasGoogleAuth, hasPasskeys].filter(Boolean).length,
    [isEmailSatisfied, hasMobile, hasGoogleAuth, hasPasskeys],
  );

  const verifyWithPasskey = useCallback(async () => {
    try {
      const optionsResult = await AuthService.passkeyGetStepUpOptions();
      if (!optionsResult?.success || !optionsResult?.data) {
        alertErrorMessage(optionsResult?.message || "Failed to get passkey options");
        return null;
      }
      let credential;
      try {
        credential = await startAuthentication(optionsResult.data);
      } catch (webauthnError) {
        if (webauthnError?.name === "NotAllowedError") {
          alertErrorMessage("Authentication was cancelled or timed out.");
        } else {
          alertErrorMessage("Failed to authenticate with passkey.");
        }
        return null;
      }
      const verifyResult = await AuthService.passkeyVerifyStepUp(credential);
      if (verifyResult?.success) return verifyResult.data;
      alertErrorMessage(verifyResult?.message || "Passkey verification failed");
      return null;
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
      return null;
    }
  }, []);

  const handleDeleteClick = (passkey) => {
    setSelectedPasskey(passkey);
    setIsDeleteConfirmOpen(true);
  };

  // ============ Add Passkey with prereq gate ============
  // Need any 2 of: verified email, mobile, Google Authenticator, or existing passkey (same as TwofactorPage canMakeSensitiveChanges).
  const handleAddPasskeyClick = async () => {
    if (activeSecurityCount >= 2) {
      try {
        LoaderHelper.loaderStatus(true);
        const optionsResult = await AuthService.passkeyGetRegistrationOptions();
        if (!optionsResult?.success || !optionsResult?.data) {
          alertErrorMessage(optionsResult?.message || "Failed to get registration options");
          return;
        }
        let credential;
        try {
          credential = await startRegistration(optionsResult.data);
        } catch (webauthnError) {
          if (webauthnError?.name === "NotAllowedError") {
            alertErrorMessage("Registration was cancelled or timed out. Please try again.");
          } else if (webauthnError?.name === "InvalidStateError") {
            alertErrorMessage("This passkey is already registered on this device.");
          } else {
            alertErrorMessage("Failed to create passkey. Please try again.");
          }
          return;
        }
        const projectName = process.env.REACT_APP_PROJECT_NAME || "Exchange";
        const defaultName = emailId ? `${projectName} - ${emailId.split("@")[0]}` : `${projectName} Passkey`;
        const verifyResult = await AuthService.passkeyVerifyRegistration(credential, defaultName);
        if (verifyResult?.success) {
          alertSuccessMessage(verifyResult?.message || "Passkey added successfully!");
          fetchPasskeys();
          handleUserDetails();
          navigate("/user_profile/security", { replace: true });
        } else {
          alertErrorMessage(verifyResult?.message || "Failed to register passkey");
        }
      } catch (error) {
        alertErrorMessage(error?.message || "Something went wrong");
      } finally {
        LoaderHelper.loaderStatus(false);
      }
      return;
    }
    // Fewer than 2 methods: show missing options (Email, Phone, Google Authenticator)
    setIsAddPasskeyOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPasskey) return;
    try {
      setIsDeleting(true);
      LoaderHelper.loaderStatus(true);

      // Verify identity with passkey first (WebAuthn)
      const vr = await verifyWithPasskey();
      if (!vr) return;

      const result = await AuthService.passkeyDelete(selectedPasskey._id, "passkey", null, vr.userId);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Passkey deleted successfully");
        setIsDeleteConfirmOpen(false);
        setSelectedPasskey(null);
        fetchPasskeys();
        handleUserDetails();
        navigate("/user_profile/security", { replace: true });
      } else {
        alertErrorMessage(result?.message || "Failed to delete passkey");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsDeleting(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  // List only methods the user can still add (so they can reach 2+ factors). Order matches original: Email, Phone, GA.
  const availableMethods = useMemo(() => {
    const list = [];
    if (!isEmailSatisfied) {
      list.push({
        id: "email",
        label: "Email",
        icon: "/images/email_icon.svg",
        onClick: () => {
          setIsAddPasskeyOpen(false);
          navigate("/user_profile/security/emailVerification");
        },
      });
    }
    if (!hasMobile) {
      list.push({
        id: "phone",
        label: "Phone Number",
        icon: "/images/security/phone_icon2.svg",
        onClick: () => {
          setIsAddPasskeyOpen(false);
          navigate("/user_profile/security/smsVerification");
        },
      });
    }
    if (!hasGoogleAuth) {
      list.push({
        id: "totp",
        label: "Google Authenticator",
        icon: "/images/security/authenticator_icon2.svg",
        onClick: () => {
          setIsAddPasskeyOpen(false);
          navigate("/user_profile/security/bindGoogleAuthenticator");
        },
      });
    }
    return list;
  }, [isEmailSatisfied, hasMobile, hasGoogleAuth, navigate]);

  const [isAddPasskeyOpen, setIsAddPasskeyOpen] = useState(false);
  const [isVerifyEmailOpen, setIsVerifyEmailOpen] = useState(false);
  const [isSecurityRiskMethodsOpen, setIsSecurityRiskMethodsOpen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);

  const handleOtpChange = (idx, e) => {
    const raw = e.target.value ?? "";
    const digit = String(raw).replace(/\D/g, "").slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) {
      window.requestAnimationFrame(() => {
        document.getElementById(`passkey-otp-${idx + 1}`)?.focus();
      });
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      window.requestAnimationFrame(() => {
        document.getElementById(`passkey-otp-${idx - 1}`)?.focus();
      });
    }
  };

  const isOtpComplete = otp.every((d) => d && d.length === 1);

  const handleVerifyEmailConfirm = () => {
    if (!isOtpComplete) return;
    // Wire AuthService email/passkey verification here when API is ready.
    alertSuccessMessage("Email verified successfully.");
    setIsVerifyEmailOpen(false);
    setIsSecurityRiskMethodsOpen(false);
    setOtp(["", "", "", "", "", ""]);
  };

  const verifyEmailOtherMethods = useMemo(() => {
    const list = [];
    if (hasPasskeys) {
      list.push({
        id: "passkey",
        label: "Passkey",
        icon: "/images/security/passkey_icon.svg",
        onClick: async () => {
          setIsSecurityRiskMethodsOpen(false);
          const ok = await verifyWithPasskey();
          if (ok) {
            alertSuccessMessage("Email verified successfully.");
            setIsVerifyEmailOpen(false);
            setOtp(["", "", "", "", "", ""]);
          }
        },
      });
    }
    if (hasMobile) {
      list.push({
        id: "phone",
        label: "Phone Number",
        icon: "/images/security/phone_icon2.svg",
        onClick: () => {
          setIsSecurityRiskMethodsOpen(false);
          setIsVerifyEmailOpen(false);
          navigate("/user_profile/security/smsVerification");
        },
      });
    }
    if (hasGoogleAuth) {
      list.push({
        id: "totp",
        label: "Google Authenticator",
        icon: "/images/security/authenticator_icon2.svg",
        onClick: () => {
          setIsSecurityRiskMethodsOpen(false);
          setIsVerifyEmailOpen(false);
          navigate("/user_profile/security/bindGoogleAuthenticator");
        },
      });
    }
    list.push({
      id: "email",
      label: "Email verification",
      icon: "/images/email_icon.svg",
      onClick: () => {
        setIsSecurityRiskMethodsOpen(false);
        setIsVerifyEmailOpen(false);
        navigate("/user_profile/security/emailVerification");
      },
    });
    return list;
  }, [hasPasskeys, hasMobile, hasGoogleAuth, navigate, verifyWithPasskey]);

  return (
    <main className="passkey-page" aria-labelledby="passkey-title">
      <nav className="passkey-page__crumbs" aria-label="Breadcrumb">
        <ol className="passkey-page__crumbList">
          <li className="passkey-page__crumbItem">
            <button type="button" className="passkey-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="passkey-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="passkey-page__crumbItem passkey-page__crumbItem--active" aria-current="page">
            Passkey
          </li>
        </ol>
      </nav>

      <div className="passkey-page__header">
        <h1 className="passkey-page__title">
          Passkey
        </h1>
        <p className="passkey-page__subtitle">
          Passkey keeps your account safe by protecting it from threats like phishing attacks. It also provides a more secure and
          convenient way to log in. <span className="passkey-page__learn">Learn More &gt;</span>
        </p>
      </div>


      {hasPasskeys ? (
        <section className="passkey-page__manage" aria-label="Registered passkeys">
          <div className="passkey-page__manageHeader">
            <h2 className="passkey-page__manageTitle">
              Registered Passkeys ({passkeys.length})
            </h2>
            <button type="button" className="passkey-page__addMore" onClick={handleAddPasskeyClick}>
              + Add Another
            </button>
          </div>

          {isLoadingPasskeys ? (
            <div className="passkey-page__empty">Loading…</div>
          ) : (
            <ul className="passkey-page__list">
              {passkeys.map((pk) => (
                <li key={pk._id} className="passkey-page__listItem">
                  <div className="passkey-page__listMain">
                    <span className="passkey-page__listIcon" aria-hidden="true">
                      <i className="ri-fingerprint-line" />
                    </span>
                    <div className="passkey-page__listBody">
                      <div className="passkey-page__listName">{pk.name || "Passkey"}</div>
                      <div className="passkey-page__listMeta">
                        {pk.deviceInfo?.browser || "Unknown"} • {pk.deviceInfo?.os || "Unknown"}
                      </div>
                      <div className="passkey-page__listMeta">
                        Added {pk.createdAt ? new Date(pk.createdAt).toLocaleDateString() : "—"}
                        {pk.lastUsedAt ? ` • Last used ${new Date(pk.lastUsedAt).toLocaleDateString()}` : ""}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="passkey-page__delete"
                    onClick={() => handleDeleteClick(pk)}
                    disabled={isDeleting}
                    aria-label={`Delete ${pk.name || "passkey"}`}
                  >
                    <i className="ri-delete-bin-line" aria-hidden="true" />
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <section className="passkey-page__content" aria-label="Passkey benefits">
          <div className="passkey-page__artWrap" aria-hidden="true">
            <img className="passkey-page__art light_img" src="/images/passkey_vector_light.svg" alt="" />
            <img className="passkey-page__art dark_img" src="/images/passkey_vector.svg" alt="" />
          </div>

          <ul className="passkey-page__benefits">
            <li className="passkey-page__benefit">
              <span className="passkey-page__benefitIcon" aria-hidden="true">

                <img src="/images/security/passkey_icon.svg" alt="" />
              </span>
              <div className="passkey-page__benefitText">
                <div className="passkey-page__benefitTitle">High Security</div>
                <div className="passkey-page__benefitDesc">Protect accounts from traditional password theft risks</div>
              </div>
            </li>

            <li className="passkey-page__benefit">
              <span className="passkey-page__benefitIcon" aria-hidden="true">
                <img src="/images/security/passkey_icon2.svg" alt="" />
              </span>
              <div className="passkey-page__benefitText">
                <div className="passkey-page__benefitTitle">Easy Verification</div>
                <div className="passkey-page__benefitDesc">Verify in one tap, free from remembering complex passwords</div>
              </div>
            </li>

            <li className="passkey-page__benefit">
              <span className="passkey-page__benefitIcon" aria-hidden="true">
                <img src="/images/security/passkey_icon3.svg" alt="" />
              </span>
              <div className="passkey-page__benefitText">
                <div className="passkey-page__benefitTitle">Multi-Device</div>
                <div className="passkey-page__benefitDesc">Use passkey across devices seamlessly</div>
              </div>
            </li>
          </ul>

          <button type="button" className="passkey-page__cta" onClick={handleAddPasskeyClick}>
            Add a Passkey
          </button>
        </section>
      )}

      {isDeleteConfirmOpen ? (
        <div className="passkey-modal-overlay" role="presentation">
          <div className="passkey-modal" role="dialog" aria-modal="true" aria-labelledby="passkey-delete-title">
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={() => {
                if (isDeleting) return;
                setIsDeleteConfirmOpen(false);
                setSelectedPasskey(null);
              }}
            >
              ×
            </button>

            <div className="passkey-modal__art" aria-hidden="true">
              <img className="dark_img" src="/images/security/security_risk_vector.svg" alt="" />
              <img className="light_img" src="/images/security/security_risk_vector_light.svg" alt="" />
            </div>

            <h2 id="passkey-delete-title" className="passkey-modal__title">
              Remove Passkey?
            </h2>
            <p className="passkey-modal__subtitle">
              You&apos;re about to delete <strong>&ldquo;{selectedPasskey?.name || "this passkey"}&rdquo;</strong>. For your safety,
              please authenticate with your passkey to confirm.
            </p>

            <button
              type="button"
              className="passkey-modal__danger"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Removing…" : "Authenticate & Remove"}
            </button>

            <button
              type="button"
              className="passkey-verify-modal__unable"
              onClick={() =>
                navigate("/asset_managemnet/security_verification_unavailable", {
                  state: { preselectMethods: ["passkeys"] },
                })
              }
            >
              Security verification unavailable?
            </button>

            <button
              type="button"
              className="passkey-modal__cancel"
              onClick={() => {
                setIsDeleteConfirmOpen(false);
                setSelectedPasskey(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {isAddPasskeyOpen ? (
        <div className="passkey-modal-overlay" role="presentation">
          <div
            className="passkey-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="passkey-modal-title"
          >
            <button type="button" className="passkey-modal__close" aria-label="Close" onClick={() => setIsAddPasskeyOpen(false)}>
              ×
            </button>

            <div className="passkey-modal__art" aria-hidden="true">
              <img className="dark_img" src="/images/security/security_risk_vector.svg" alt="" />
              <img className="light_img" src="/images/security/security_risk_vector_light.svg" alt="" />
            </div>

            <h2 id="passkey-modal-title" className="passkey-modal__title">
              Security Risk Warning
            </h2>
            <p className="passkey-modal__subtitle">
              To enhance your account security, please activate at least one additional verification method.
            </p>

            <div className="passkey-modal__list" role="list">
              {availableMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  className="passkey-modal__item"
                  role="listitem"
                  onClick={method.onClick}
                >
                  <span className="passkey-modal__itemLeft">
                    <span className="passkey-modal__itemIcon" aria-hidden="true">
                      <img src={method.icon} alt="" />
                    </span>
                    <span className="passkey-modal__itemText">{method.label}</span>
                  </span>
                  <span className="passkey-modal__chev" aria-hidden="true">
                    ›
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {isVerifyEmailOpen ? (
        <div className="passkey-modal-overlay" role="presentation">
          <div
            className="passkey-modal passkey-verify-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="passkey-verify-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={() => {
                setIsVerifyEmailOpen(false);
                setIsSecurityRiskMethodsOpen(false);
              }}
            >
              ×
            </button>

            <h2 id="passkey-verify-title" className="passkey-verify-modal__title">
              Verify Your Email
            </h2>
            <p className="passkey-verify-modal__subtitle">
              The verification code has been sent to your email j***9@gmail.com, valid for 10 minutes.
            </p>

            <div className="passkey-verify-modal__otpRow" aria-label="Verification code">
              <input
                id="passkey-otp-0"
                className="passkey-verify-modal__otp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                autoComplete="one-time-code"
                aria-label="Digit 1"
                value={otp[0]}
                onChange={(e) => handleOtpChange(0, e)}
                onKeyDown={(e) => handleOtpKeyDown(0, e)}
              />
              <input
                id="passkey-otp-1"
                className="passkey-verify-modal__otp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                aria-label="Digit 2"
                value={otp[1]}
                onChange={(e) => handleOtpChange(1, e)}
                onKeyDown={(e) => handleOtpKeyDown(1, e)}
              />
              <input
                id="passkey-otp-2"
                className="passkey-verify-modal__otp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                aria-label="Digit 3"
                value={otp[2]}
                onChange={(e) => handleOtpChange(2, e)}
                onKeyDown={(e) => handleOtpKeyDown(2, e)}
              />
              <input
                id="passkey-otp-3"
                className="passkey-verify-modal__otp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                aria-label="Digit 4"
                value={otp[3]}
                onChange={(e) => handleOtpChange(3, e)}
                onKeyDown={(e) => handleOtpKeyDown(3, e)}
              />
              <input
                id="passkey-otp-4"
                className="passkey-verify-modal__otp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                aria-label="Digit 5"
                value={otp[4]}
                onChange={(e) => handleOtpChange(4, e)}
                onKeyDown={(e) => handleOtpKeyDown(4, e)}
              />
              <input
                id="passkey-otp-5"
                className="passkey-verify-modal__otp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                aria-label="Digit 6"
                value={otp[5]}
                onChange={(e) => handleOtpChange(5, e)}
                onKeyDown={(e) => handleOtpKeyDown(5, e)}
              />
            </div>

            <div className="passkey-verify-modal__links">
              <button type="button" className="passkey-verify-modal__link">
                Resend
              </button>
              <button type="button" className="passkey-verify-modal__link passkey-verify-modal__link--paste">
                Paste{" "}
                <span aria-hidden="true">
                  <i className="ri-file-copy-fill" />
                </span>
              </button>
            </div>

            <button
              type="button"
              className="passkey-verify-modal__confirm"
              disabled={!isOtpComplete}
              onClick={handleVerifyEmailConfirm}
            >
              Confirm
            </button>

            <div className="passkey-verify-modal__footerRow">
              {verifyEmailOtherMethods.length > 0 ? (
                <button
                  type="button"
                  className="passkey-verify-modal__chooseOther"
                  onClick={() => setIsSecurityRiskMethodsOpen(true)}
                >
                  Choose other verification method
                </button>
              ) : null}
              <button
                type="button"
                className="passkey-verify-modal__unable"
                onClick={() =>
                navigate("/asset_managemnet/security_verification_unavailable", {
                  state: { preselectMethods: ["passkeys"] },
                })
              }
              >
                Security verification unavailable?
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isSecurityRiskMethodsOpen && verifyEmailOtherMethods.length > 0 ? (
        <div className="passkey-modal-overlay passkey-modal-overlay--onTop" role="presentation">
          <div
            className="passkey-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="passkey-sec-risk-other-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={() => setIsSecurityRiskMethodsOpen(false)}
            >
              ×
            </button>
            <div className="passkey-modal__art" aria-hidden="true">
              <img src="/images/security/security_risk_vector.svg" alt="" />
            </div>
            <h2 id="passkey-sec-risk-other-title" className="passkey-modal__title">
              Security Risk Warning
            </h2>
            <p className="passkey-modal__subtitle">
              To enhance your account security, please activate at least one additional verification method.
            </p>
            <div className="passkey-modal__list" role="list">
              {verifyEmailOtherMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  className="passkey-modal__item"
                  role="listitem"
                  onClick={method.onClick}
                >
                  <span className="passkey-modal__itemLeft">
                    <span className="passkey-modal__itemIcon" aria-hidden="true">
                      <img src={method.icon} alt="" />
                    </span>
                    <span className="passkey-modal__itemText">{method.label}</span>
                  </span>
                  <span className="passkey-modal__chev" aria-hidden="true">
                    ›
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="passkey-verify-modal__unable"
              style={{ marginTop: 16 }}
              onClick={() =>
                navigate("/asset_managemnet/security_verification_unavailable", {
                  state: { preselectMethods: ["passkeys"] },
                })
              }
            >
              Security verification unavailable?
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default PasskeyPage;

